/**
 * FS-14-02 영상 — 화면 순수 함수(상태 알약 · 길이 · 크기 · 만료 · 안내 줄). SCR v0.7 검토 4-1~4-3 · v07-16/17.
 */
import type { VideoJob, VideoStatus } from "../types";

/** 상태 알약 5종 순서(와이어 .state — 대기 · 렌더 중 · 다시 시도 중 · 만들지 못했어요 · 완료) */
export const VIDEO_STEPS: readonly VideoStatus[] = [
  "queued",
  "rendering",
  "retrying",
  "dead",
  "done",
];

/** 아직 끝나지 않은 작업(목록을 자주 다시 읽는다) */
export const isPending = (s: VideoStatus) =>
  s === "queued" || s === "rendering" || s === "retrying";

/** 리플레이 보존이 끝났나(FE-504) — expiresAt 이 지났으면 */
export function isExpiredJob(
  j: Pick<VideoJob, "expiresAt"> & Partial<Pick<VideoJob, "feCode">>,
  now = Date.now(),
): boolean {
  if (j.feCode === "FE-504") return true;
  if (!j.expiresAt) return false;
  const t = Date.parse(j.expiresAt);
  return Number.isFinite(t) && t <= now;
}

/** 내려받을 수 있나 — 완료 + 만료 전 */
export const canDownload = (j: VideoJob, now = Date.now()) =>
  j.status === "done" && !isExpiredJob(j, now);

/** 다시 요청할 수 있나 — 만들지 못했어요(dead) + 만료 전 */
export const canRetry = (j: VideoJob, now = Date.now()) =>
  j.status === "dead" && !isExpiredJob(j, now);

/** 초 → m:ss(한 시간 넘으면 h:mm:ss) */
export function fmtDuration(s: number): string {
  const t = Math.max(0, Math.round(s));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const ss = String(t % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** 바이트 → '48 MB' · '820 KB' · '1.2 GB' */
export function fmtSize(bytes: number): string {
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** 날짜(현지) — '2027. 3. 25.' */
export function fmtDate(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t)
    ? new Date(t).toLocaleDateString(locale, { year: "numeric", month: "numeric", day: "numeric" })
    : null;
}

/** 목록 순서 — 손이 필요한 것(만들지 못했어요)이 먼저, 그다음 진행 중, 완료는 최근 것부터 */
export function sortJobs(xs: readonly VideoJob[]): VideoJob[] {
  const rank: Record<VideoStatus, number> = {
    dead: 0,
    retrying: 1,
    rendering: 2,
    queued: 3,
    done: 4,
  };
  return [...xs].sort(
    (a, b) =>
      rank[a.status] - rank[b.status] ||
      (b.finishedAt ?? b.createdAt ?? "").localeCompare(a.finishedAt ?? a.createdAt ?? "") ||
      a.jobId.localeCompare(b.jobId),
  );
}

/** 상단 바 칩 셈 — 완료 · 진행 중(대기·렌더·재시도) · 손이 필요한 것 */
export function counts(xs: readonly VideoJob[]) {
  return {
    done: xs.filter((j) => j.status === "done").length,
    pending: xs.filter((j) => isPending(j.status)).length,
    dead: xs.filter((j) => j.status === "dead").length,
  };
}
